import { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import Seo from '../components/Seo';
import assetPath from '../utils/assetPath';

const copy = {
  en: {
    title: 'Contact SΛLTY',
    intro:
      'For partnerships, media, or general questions, use the form or reach us through the channels below.',
    form: {
      name: 'Name / Callsign',
      email: 'Email',
      message: 'Message',
      cta: 'Send Message',
      notice: 'For faster responses, reach us on Instagram or Discord.',
    },
    channels: [
      {
        title: 'Instagram',
        body: 'Follow the feed and message us for media or partnership inquiries.',
        action: '@salty.airsoft',
        href: 'https://www.instagram.com/salty.airsoft/',
      },
      {
        title: 'Discord',
        body: 'Join the community server and chat with the team.',
        action: 'Join server',
        href: 'https://discord.gg/ncMBPzqVza',
      },
    ],
  },
  fr: {
    title: 'Contact SΛLTY',
    intro:
      'Pour les partenariats, les médias ou toute question, utilisez le formulaire ou passez par les canaux ci-dessous.',
    form: {
      name: 'Nom / Callsign',
      email: 'Email',
      message: 'Message',
      cta: 'Envoyer le message',
      notice: 'Pour une réponse plus rapide, contactez-nous sur Instagram ou Discord.',
    },
    channels: [
      {
        title: 'Instagram',
        body: 'Suivez le feed et écrivez-nous pour les demandes média ou partenariat.',
        action: '@salty.airsoft',
        href: 'https://www.instagram.com/salty.airsoft/',
      },
      {
        title: 'Discord',
        body: 'Rejoignez le serveur communauté et échangez avec l’équipe.',
        action: 'Rejoindre le serveur',
        href: 'https://discord.gg/ncMBPzqVza',
      },
    ],
  },
};

const WEBHOOK_URL =
  'https://discord.com/api/webhooks/1449901584547975300/V36BH2joJVpZauuhUGW0SH82jSmc6aO_1GXg4VnIhXbGemHrXE6tDDEBscAT9MTQWPWN';

const Contact = () => {
  const { language } = useAppContext();
  const t = copy[language];
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('sending');
    try {
      const embeds = [
        {
          title: 'New contact message',
          description: form.message || '_No message provided_',
          color: 0x23c4ff,
          author: {
            name: form.name || 'Guest',
          },
          fields: [
            {
              name: '📧 Email',
              value: form.email || 'n/a',
              inline: true,
            },
            {
              name: '🌐 Language',
              value: language.toUpperCase(),
              inline: true,
            },
          ],
          footer: {
            text: 'salty-squad.fr/contact',
          },
          timestamp: new Date().toISOString(),
        },
      ];

      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: 'Salty Contact Bot',
          embeds,
        }),
      });

      if (!response.ok && response.status !== 204) {
        throw new Error(`Discord webhook returned ${response.status}`);
      }

      setStatus('success');
      setForm({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Unable to relay contact form message', error);
      setStatus('error');
    }
  };

  return (
    <>
      <Seo title="Contact" description={t.intro} image={assetPath('/assets/logo-salty.png')} url="https://salty-squad.fr/contact" />
      <section className="contact-page">
        <div className="contact-header">
          <p className="unit">{language === 'fr' ? 'Contact' : 'Contact'}</p>
          <h1>{t.title}</h1>
          <p>{t.intro}</p>
        </div>
        <div className="contact-grid">
          <form className="contact-form" onSubmit={handleSubmit}>
            <label>
              <span>{t.form.name}</span>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
              />
            </label>
            <label>
              <span>{t.form.email}</span>
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" required />
            </label>
            <label>
              <span>{t.form.message}</span>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows="5"
                placeholder={language === 'fr' ? 'Votre message' : 'Your message'}
                required
              />
            </label>
            <button type="submit" className="btn primary" disabled={status === 'sending'}>
              {t.form.cta}
            </button>
            <p className="form-notice">{t.form.notice}</p>
            {status === 'success' && (
              <p className="form-status success" role="status" aria-live="polite">
                {language === 'fr' ? 'Votre message a été envoyé avec succès.' : 'Your message has been sent successfully.'}
              </p>
            )}
            {status === 'error' && (
              <p className="form-status error" role="status" aria-live="assertive">
                {language === 'fr'
                  ? 'Erreur lors de l’envoi. Réessayez ou contactez-nous via Discord.'
                  : 'Failed to send. Please try again or contact us on Discord.'}
              </p>
            )}
          </form>
          <div className="contact-channels">
            {t.channels.map((channel) => (
              <article className="contact-card" key={channel.title}>
                <h3>{channel.title}</h3>
                <p>{channel.body}</p>
                <a href={channel.href} target="_blank" rel="noreferrer" className="contact-card-link">
                  {channel.action}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
